import { format } from "date-fns";
import subDays from "date-fns/subDays";

/**
 * e.g. "Apr 24 → May 24, 2026" when both dates share a year;
 * "Dec 31, 2025 → Jan 2, 2026" when years differ.
 */
export function formatDateRangeSummary(
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
): string {
  if (!startDate || !endDate) {
    return "No dates";
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, "MMM d")} → ${format(end, "MMM d, yyyy")}`;
  }

  return `${format(start, "MMM d, yyyy")} → ${format(end, "MMM d, yyyy")}`;
}

export const createDefaultOperationalDateRange = () => ({
  startDate: subDays(new Date(), 30),
  endDate: new Date(),
});

export const cleanFiltersForRequest = <T extends Record<string, unknown>>(
  filters: T = {} as T,
): T => {
  const cleaned = { ...filters };

  if (!cleaned.startDate || !cleaned.endDate) {
    delete cleaned.startDate;
    delete cleaned.endDate;
  }

  return cleaned;
};

type SelectOption = { value: number | string; label: string };

export type MultiFilterValue =
  | Array<number | string>
  | SelectOption[]
  | SelectOption
  | null
  | undefined;

const MULTI_SELECT_FILTER_KEYS = [
  "product",
  "category",
  "brand",
  "customer",
  "orderStatus",
  "status",
  "certification",
  "recipe",
  "supervisor",
  "employee1",
  "employee2",
] as const;

export function excludeAllOption<T extends SelectOption>(
  options: T[] = [],
): T[] {
  return options.filter((option) => option.value !== -1);
}

function extractFilterValues(
  selection: MultiFilterValue,
): Array<number | string> {
  if (!Array.isArray(selection)) {
    return [];
  }

  return selection
    .map((item) =>
      typeof item === "object" && item != null && "value" in item
        ? item.value
        : item,
    )
    .filter(
      (value): value is number | string =>
        value !== -1 && value != null && value !== "",
    );
}

export function isMultiFilterActive(selection: MultiFilterValue): boolean {
  return extractFilterValues(selection).length > 0;
}

function serializeMultiSelectValue(
  selection: MultiFilterValue,
): { value: number | string | Array<number | string> } | null | undefined {
  if (Array.isArray(selection)) {
    const values = extractFilterValues(selection);

    if (!values.length) {
      return { value: -1 };
    }

    return { value: values.length === 1 ? values[0] : values };
  }

  if (selection && typeof selection === "object" && "value" in selection) {
    return selection;
  }

  return selection ?? undefined;
}

/** Maps in-app multi-select arrays to API `{ value }` / `{ value: id[] }` shape. */
export function serializeMultiSelectFiltersForRequest<
  T extends Record<string, unknown>,
>(filters: T = {} as T): T {
  const serialized: Record<string, unknown> = { ...filters };

  for (const key of MULTI_SELECT_FILTER_KEYS) {
    if (key in serialized) {
      serialized[key] = serializeMultiSelectValue(
        serialized[key] as MultiFilterValue,
      );
    }
  }

  return serialized as T;
}
